import type { DrawingEntity, EntityType } from '../contract/types.js';
import { matchEntities, groupByType } from '../match/index.js';
import { sampledPath, symmetricHausdorff } from '../match/geometry.js';

/**
 * Identity, rather than counts.
 *
 * Stage 2 of the accuracy pipeline reports precision and recall per entity type.
 * That answers *how many* and cannot answer *which*, and the two come apart in
 * exactly the case this file exists for: an entity that leaves `drawing.json` as
 * a `LINE` and arrives in the DXF as an `LWPOLYLINE` is **one** entity, correct
 * in every coordinate, and the per-type figures report it as a `LINE` that went
 * missing plus an `LWPOLYLINE` that appeared from nowhere. Two defects, neither
 * of them real, and the actual fault — a type changed in transit — appears
 * nowhere in the report.
 *
 * Matching per type is the right rule for scoring and cannot see across types by
 * construction, so the cross-type pass is here rather than in the matcher.
 *
 * The answer is a **list of faults with places on the drawing**, not a ratio: a
 * reader who has been told something is missing wants to know what and where,
 * and a figure of 0.98 tells them neither.
 */

export interface EntityRef {
  id: string;
  type: EntityType;
  /** `[minX, minY, maxX, maxY]`, so a fault can be pointed at rather than described. */
  where: readonly [number, number, number, number];
}

export interface TypeChange {
  declared: EntityRef;
  delivered: EntityRef;
}

export interface IdentityReport {
  /** Declared by `drawing.json` and absent from the DXF. */
  missing: EntityRef[];
  /** Carried by the DXF and declared by nothing. */
  extra: EntityRef[];
  /** The same shape in the same place, under a different type. */
  wronglyTyped: TypeChange[];
  /** Stored on one page and claiming to come from another. */
  misattributed: EntityRef[];
  /** Every fault, in one number, so a reader has something to check against zero. */
  faults: number;
  /** How many entities `drawing.json` declared. The denominator. */
  declared: number;
  /**
   * How many of them were found in the DXF as themselves.
   *
   * "None missing, none added, none under another type" is a claim about three
   * empty lists — and three empty lists are also what a verifier that checked
   * nothing produces. `followed` is the same statement with a denominator, and
   * it is a statement about the *declared* side only, so an entity the DXF
   * invented can never pay for one that did not arrive.
   */
  followed: number;
  ok: boolean;
}

export interface IdentityOptions {
  toleranceMm: number;
  arcAngleToleranceDeg?: number;
  /** The fidelity condition stage 2 applies, so both agree on what a match is. */
  alsoRequire?: (a: DrawingEntity, b: DrawingEntity) => boolean;
  /**
   * The document's pages, when the page attribution is to be checked too.
   *
   * Separate from `declared` because that side is flattened into drawing space
   * and has lost which sheet it came from, and because the check is about the
   * *document*, not about the delivery.
   */
  pages?: readonly (readonly DrawingEntity[])[];
}

function boxOf(e: DrawingEntity): readonly [number, number, number, number] {
  const points = sampledPath(e);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  return Number.isFinite(minX) ? [minX, minY, maxX, maxY] : [0, 0, 0, 0];
}

const refOf = (e: DrawingEntity): EntityRef => ({ id: e.id, type: e.type, where: boxOf(e) });

export function verifyIdentity(
  declared: readonly DrawingEntity[],
  delivered: readonly DrawingEntity[],
  options: IdentityOptions,
): IdentityReport {
  const leftoverDeclared: DrawingEntity[] = [];
  const leftoverDelivered: DrawingEntity[] = [];
  /**
   * Entities on the wrong sheet, from either of the two directions it can happen.
   *
   * The `options.pages` pass below compares an entity's own `pageIndex` against
   * the position it is stored at. Worth keeping and **not** worth trusting on
   * its own: in production both values come from the same loop variable in
   * `readPage(page, index)`, so it is an expression compared with itself and can
   * only ever report zero — the shape of the operator-ledger tautology this
   * project has already been caught by once. The delivered-side check in the
   * matching loop is the one with something to find.
   */
  const misattributed: EntityRef[] = [];

  // Same rule as stage 2, per type, so the two agree about what matched.
  const declaredByType = groupByType(declared);
  const deliveredByType = groupByType(delivered);
  const types = new Set<EntityType>([...declaredByType.keys(), ...deliveredByType.keys()]);

  for (const type of types) {
    const d = declaredByType.get(type) ?? [];
    const got = deliveredByType.get(type) ?? [];
    const result = matchEntities(got, d, options.toleranceMm, {
      ...(options.arcAngleToleranceDeg === undefined
        ? {}
        : { angleToleranceDeg: options.arcAngleToleranceDeg }),
      ...(options.alsoRequire === undefined ? {} : { alsoRequire: options.alsoRequire }),
    });
    for (const i of result.unmatchedTruth) leftoverDeclared.push(d[i]!);
    for (const i of result.unmatchedProduced) leftoverDelivered.push(got[i]!);

    // The sheet the DXF put it on, against the sheet it came from.
    //
    // This is the half of page attribution that can actually go wrong. DXF has
    // no pages, so the writer separates sheets by layer, and an entity that
    // leaves sheet two and arrives on `PAGE_1` is on the wrong sheet in the file
    // the customer opens — with every coordinate correct, every type correct,
    // and every per-type figure at 1.000.
    for (const pair of result.pairs) {
      const declaredEntity = d[pair.truthIndex]!;
      const deliveredEntity = got[pair.producedIndex]!;
      if (sheetOfLayer(deliveredEntity.layer) !== declaredEntity.source.pageIndex) {
        misattributed.push(refOf(declaredEntity));
      }
    }
  }

  const { wronglyTyped, missing, extra } = pairAcrossTypes(
    leftoverDeclared,
    leftoverDelivered,
    options.toleranceMm,
  );

  (options.pages ?? []).forEach((entities, index) => {
    for (const e of entities) {
      if (e.source.pageIndex !== index) misattributed.push(refOf(e));
    }
  });

  const faults = missing.length + extra.length + wronglyTyped.length + misattributed.length;
  // Counted off the declared side: everything declared, less the ones that were
  // not found under their own type. A type change is not a follow-through — the
  // entity arrived as something else, which is the fault being reported.
  const followed = declared.length - missing.length - wronglyTyped.length;
  return {
    missing,
    extra,
    wronglyTyped,
    misattributed,
    faults,
    declared: declared.length,
    followed,
    ok: faults === 0,
  };
}

/**
 * The sheet a DXF layer attributes an entity to, zero-based.
 *
 * `layerFor` in the writer qualifies a layer only when the document has more
 * than one page — `PAGE_2`, `PAGE_2_DIMENSIONS` — so a bare layer name is sheet
 * one rather than an unattributed entity, and a single-sheet drawing must not
 * read as a whole drawing misfiled.
 */
function sheetOfLayer(layer: string): number {
  const m = /^PAGE_(\d+)(?:_.*)?$/.exec(layer);
  return m ? Number(m[1]) - 1 : 0;
}

/**
 * What is left after per-type matching, paired across types by geometry.
 *
 * The test is the **symmetric** Hausdorff distance, not "lies on": a fragment of
 * a shape lies on it perfectly in one direction, so a one-directional test calls
 * a polyline split into two lines a pair of type changes. Symmetric means the
 * two shapes have to describe each other, which is what "the same entity under
 * another name" means.
 *
 * Greedy on the closest pair, and each entity is consumed once, so two entities
 * in range of each other produce two faults rather than four.
 */
function pairAcrossTypes(
  declared: readonly DrawingEntity[],
  delivered: readonly DrawingEntity[],
  tolerance: number,
): { wronglyTyped: TypeChange[]; missing: EntityRef[]; extra: EntityRef[] } {
  const candidates: { d: number; g: number; distance: number }[] = [];
  declared.forEach((a, d) => {
    delivered.forEach((b, g) => {
      if (a.type === b.type) return;
      const distance = symmetricHausdorff(sampledPath(a), sampledPath(b));
      if (distance <= tolerance) candidates.push({ d, g, distance });
    });
  });
  candidates.sort((x, y) => x.distance - y.distance);

  const usedDeclared = new Set<number>();
  const usedDelivered = new Set<number>();
  const wronglyTyped: TypeChange[] = [];
  for (const c of candidates) {
    if (usedDeclared.has(c.d) || usedDelivered.has(c.g)) continue;
    usedDeclared.add(c.d);
    usedDelivered.add(c.g);
    wronglyTyped.push({ declared: refOf(declared[c.d]!), delivered: refOf(delivered[c.g]!) });
  }

  return {
    wronglyTyped,
    missing: declared.filter((_, i) => !usedDeclared.has(i)).map(refOf),
    extra: delivered.filter((_, i) => !usedDelivered.has(i)).map(refOf),
  };
}
